import { RecursiveCharacterTextSplitter } from 'langchain/text_splitter';
import { Document } from 'langchain/document';

export interface ChunkMetadata {
  documentId: string;
  documentName: string;
  pageNumber?: number;
  chunkIndex: number;
  totalChunks?: number;
  startChar?: number;
  endChar?: number;
}

export interface DocumentChunk {
  content: string;
  metadata: ChunkMetadata;
  tokenCount?: number;
}

export interface ChunkingOptions {
  chunkSize?: number;
  chunkOverlap?: number;
  separators?: string[];
  keepSeparator?: boolean;
}

export class ChunkingService {
  private static instance: ChunkingService;
  private defaultOptions: Required<ChunkingOptions> = {
    chunkSize: 1000, // Characters per chunk
    chunkOverlap: 200, // Overlap between chunks for context preservation
    separators: ['\n\n', '\n', '.', '!', '?', ';', ':', ' ', ''], // Hierarchical separators
    keepSeparator: false
  };

  private constructor() {}

  public static getInstance(): ChunkingService {
    if (!ChunkingService.instance) {
      ChunkingService.instance = new ChunkingService();
    }
    return ChunkingService.instance;
  }

  /**
   * Split a document into chunks using LangChain's RecursiveCharacterTextSplitter
   */
  public async chunkDocument(
    documentId: string,
    documentName: string,
    text: string,
    options?: ChunkingOptions
  ): Promise<DocumentChunk[]> {
    const config = { ...this.defaultOptions, ...options };

    // Create the text splitter
    const splitter = new RecursiveCharacterTextSplitter({
      chunkSize: config.chunkSize,
      chunkOverlap: config.chunkOverlap,
      separators: config.separators,
      keepSeparator: config.keepSeparator
    });

    // Split the text
    const documents = await splitter.createDocuments([text]);

    // Convert to our chunk format
    const chunks: DocumentChunk[] = documents.map((doc, index) => ({
      content: doc.pageContent,
      metadata: {
        documentId,
        documentName,
        chunkIndex: index,
        totalChunks: documents.length,
        startChar: doc.metadata.loc?.start,
        endChar: doc.metadata.loc?.end
      },
      tokenCount: this.estimateTokenCount(doc.pageContent)
    }));

    return chunks;
  }

  /**
   * Split multiple pages into chunks, preserving page boundaries
   */
  public async chunkDocumentByPages(
    documentId: string,
    documentName: string,
    pages: { pageNumber: number; text: string }[],
    options?: ChunkingOptions
  ): Promise<DocumentChunk[]> {
    const config = { ...this.defaultOptions, ...options };
    const allChunks: DocumentChunk[] = [];

    console.log(`[ChunkingService] Processing ${pages.length} pages for ${documentName}`);

    // Filter out empty pages but keep track of them
    const nonEmptyPages = pages.filter(p => p.text && p.text.trim().length > 0);

    if (nonEmptyPages.length === 0) {
      console.warn(`[ChunkingService] No text content found in any page of ${documentName}`);
      // Create at least one chunk to prevent 0 chunks issue
      return [{
        content: `[Document: ${documentName}]\n[No extractable text content found in ${pages.length} pages]\n[This may be a scanned PDF requiring OCR]`,
        metadata: {
          documentId,
          documentName,
          pageNumber: 1,
          chunkIndex: 0,
          totalChunks: 1,
          startChar: 0,
          endChar: 100
        },
        tokenCount: 50
      }];
    }

    // Create the text splitter
    const splitter = new RecursiveCharacterTextSplitter({
      chunkSize: config.chunkSize,
      chunkOverlap: config.chunkOverlap,
      separators: config.separators,
      keepSeparator: config.keepSeparator
    });

    let globalChunkIndex = 0;

    for (const page of nonEmptyPages) {
      console.log(`[ChunkingService] Processing page ${page.pageNumber} with ${page.text.length} characters`);

      // Skip pages with minimal content
      if (page.text.trim().length < 10) {
        console.log(`[ChunkingService] Skipping page ${page.pageNumber} - too little content`);
        continue;
      }

      // Split each page separately to preserve page boundaries
      const pageDocuments = await splitter.createDocuments(
        [page.text],
        [{ pageNumber: page.pageNumber }]
      );

      console.log(`[ChunkingService] Page ${page.pageNumber} split into ${pageDocuments.length} chunks`);

      // Convert to chunks with page metadata
      const pageChunks: DocumentChunk[] = pageDocuments.map((doc) => ({
        content: doc.pageContent,
        metadata: {
          documentId,
          documentName,
          pageNumber: page.pageNumber,
          chunkIndex: globalChunkIndex++,
          startChar: doc.metadata.loc?.start,
          endChar: doc.metadata.loc?.end
        },
        tokenCount: this.estimateTokenCount(doc.pageContent)
      }));

      allChunks.push(...pageChunks);
    }

    // If still no chunks, create a placeholder
    if (allChunks.length === 0) {
      console.warn(`[ChunkingService] No chunks created for ${documentName}, adding placeholder`);
      allChunks.push({
        content: `[Document: ${documentName}]\n[Processing resulted in no text chunks]\n[Document had ${pages.length} pages but minimal extractable content]`,
        metadata: {
          documentId,
          documentName,
          pageNumber: 1,
          chunkIndex: 0,
          totalChunks: 1,
          startChar: 0,
          endChar: 100
        },
        tokenCount: 50
      });
    }

    // Update total chunks count
    allChunks.forEach(chunk => {
      chunk.metadata.totalChunks = allChunks.length;
    });

    console.log(`[ChunkingService] Created ${allChunks.length} total chunks for ${documentName}`);
    return allChunks;
  }

  /**
   * Create semantic chunks based on document structure
   * This method attempts to preserve logical sections
   */
  public async createSemanticChunks(
    documentId: string,
    documentName: string,
    text: string,
    options?: ChunkingOptions
  ): Promise<DocumentChunk[]> {
    const config = {
      ...this.defaultOptions,
      ...options,
      // Use more semantic separators for better context preservation
      separators: [
        '\n## ', // Markdown headers
        '\n### ',
        '\n#### ',
        '\n\n',  // Paragraphs
        '\n',    // Lines
        '. ',    // Sentences
        ', ',    // Clauses
        ' ',     // Words
        ''       // Characters
      ]
    };

    return this.chunkDocument(documentId, documentName, text, config);
  }

  /**
   * Optimize chunks by removing too small or too large chunks
   */
  public optimizeChunks(
    chunks: DocumentChunk[],
    minSize: number = 100,
    maxSize: number = 2000
  ): DocumentChunk[] {
    const optimized: DocumentChunk[] = [];
    let buffer: DocumentChunk | null = null;

    for (const chunk of chunks) {
      const chunkLength = chunk.content.length;

      if (chunkLength < minSize) {
        // Chunk is too small, buffer it
        if (buffer) {
          // Combine with buffer
          buffer.content += '\n' + chunk.content;
          buffer.tokenCount = this.estimateTokenCount(buffer.content);
        } else {
          buffer = { ...chunk };
        }
      } else if (chunkLength > maxSize) {
        // Chunk is too large, split it further
        const subChunks = this.splitLargeChunk(chunk, maxSize);
        optimized.push(...subChunks);
        buffer = null;
      } else {
        // Chunk is optimal size
        if (buffer) {
          // Add buffered content if exists
          if (buffer.content.length >= minSize) {
            optimized.push(buffer);
          } else {
            // Combine buffer with current chunk
            chunk.content = buffer.content + '\n' + chunk.content;
            chunk.tokenCount = this.estimateTokenCount(chunk.content);
          }
          buffer = null;
        }
        optimized.push(chunk);
      }
    }

    // Don't forget the last buffer
    if (buffer && buffer.content.length > 0) {
      optimized.push(buffer);
    }

    // Re-index chunks
    optimized.forEach((chunk, index) => {
      chunk.metadata.chunkIndex = index;
      chunk.metadata.totalChunks = optimized.length;
    });

    return optimized;
  }

  /**
   * Split a large chunk into smaller pieces
   */
  private splitLargeChunk(chunk: DocumentChunk, maxSize: number): DocumentChunk[] {
    const pieces: DocumentChunk[] = [];
    const words = chunk.content.split(' ');
    let currentPiece = '';
    let pieceIndex = 0;

    for (const word of words) {
      if ((currentPiece + ' ' + word).length > maxSize && currentPiece.length > 0) {
        // Save current piece and start a new one
        pieces.push({
          content: currentPiece.trim(),
          metadata: {
            ...chunk.metadata,
            chunkIndex: chunk.metadata.chunkIndex + pieceIndex * 0.1 // Fractional index for sub-chunks
          },
          tokenCount: this.estimateTokenCount(currentPiece)
        });
        currentPiece = word;
        pieceIndex++;
      } else {
        currentPiece += (currentPiece ? ' ' : '') + word;
      }
    }

    // Add the last piece
    if (currentPiece) {
      pieces.push({
        content: currentPiece.trim(),
        metadata: {
          ...chunk.metadata,
          chunkIndex: chunk.metadata.chunkIndex + pieceIndex * 0.1
        },
        tokenCount: this.estimateTokenCount(currentPiece)
      });
    }

    return pieces;
  }

  /**
   * Add contextual overlap between chunks for better coherence
   */
  public addContextualOverlap(chunks: DocumentChunk[], overlapSentences: number = 1): DocumentChunk[] {
    const enhanced = [...chunks];

    for (let i = 0; i < enhanced.length; i++) {
      const currentChunk = enhanced[i];
      let contextPrefix = '';
      let contextSuffix = '';

      // Add context from previous chunk
      if (i > 0) {
        const prevChunk = enhanced[i - 1];
        const prevSentences = prevChunk.content.split(/[.!?]+/);
        const lastSentences = prevSentences.slice(-overlapSentences).join('. ');
        if (lastSentences.trim()) {
          contextPrefix = `[Context from previous section: ${lastSentences.trim()}...]\n\n`;
        }
      }

      // Add context from next chunk
      if (i < enhanced.length - 1) {
        const nextChunk = enhanced[i + 1];
        const nextSentences = nextChunk.content.split(/[.!?]+/);
        const firstSentences = nextSentences.slice(0, overlapSentences).join('. ');
        if (firstSentences.trim()) {
          contextSuffix = `\n\n[Continues with: ${firstSentences.trim()}...]`;
        }
      }

      // Update chunk with context
      if (contextPrefix || contextSuffix) {
        currentChunk.content = contextPrefix + currentChunk.content + contextSuffix;
        currentChunk.tokenCount = this.estimateTokenCount(currentChunk.content);
      }
    }

    return enhanced;
  }

  /**
   * Estimate token count for text (rough approximation)
   */
  private estimateTokenCount(text: string): number {
    // Rough estimation: 1 token ≈ 4 characters for English text
    return Math.ceil(text.length / 4);
  }

  /**
   * Get statistics about chunks
   */
  public getChunkStatistics(chunks: DocumentChunk[]): {
    totalChunks: number;
    averageSize: number;
    minSize: number;
    maxSize: number;
    totalTokens: number;
    averageTokens: number;
  } {
    if (chunks.length === 0) {
      return {
        totalChunks: 0,
        averageSize: 0,
        minSize: 0,
        maxSize: 0,
        totalTokens: 0,
        averageTokens: 0
      };
    }

    const sizes = chunks.map(c => c.content.length);
    const tokens = chunks.map(c => c.tokenCount || 0);

    return {
      totalChunks: chunks.length,
      averageSize: Math.round(sizes.reduce((a, b) => a + b, 0) / sizes.length),
      minSize: Math.min(...sizes),
      maxSize: Math.max(...sizes),
      totalTokens: tokens.reduce((a, b) => a + b, 0),
      averageTokens: Math.round(tokens.reduce((a, b) => a + b, 0) / tokens.length)
    };
  }
}
